import React, { useEffect, useMemo, useState } from "react";
import {
  getReportStatus,
  getVideoStatus,
  type DSAReportStatusResponse,
  type VideoStatusResponse,
} from "@/api/reportApi";
import ReportComponent from "@/modules/report/Report";

interface ReportProps {
  id?: string;
}

type ProviderProfile = {
  full_name?: string;
  username?: string;
  profile_image_url?: string;
  email?: string;
};

const POLL_INTERVAL_MS = 5000;
const PROVIDER_ENDPOINT =
  (typeof import.meta !== "undefined" && (import.meta as any)?.env?.PROVIDER_ENDPOINT) ||
  (typeof process !== "undefined" && (process.env as any)?.PROVIDER_ENDPOINT) ||
  "https://employability.ai";

const Report: React.FC<ReportProps> = ({ id }) => {
  const [report, setReport] = useState<DSAReportStatusResponse | null>(null);
  const [videoStatus, setVideoStatus] = useState<VideoStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [providerProfile, setProviderProfile] = useState<ProviderProfile | null>(null);
  const [providerProfileUrl, setProviderProfileUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setError("No report ID provided");
      setLoading(false);
      return;
    }

    let isCancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const fetchReport = async (isPoll = false) => {
      try {
        const [reportData, videoData] = await Promise.all([
          getReportStatus(id),
          getVideoStatus(id),
        ]);

        if (isCancelled) return;

        setReport(reportData);
        setVideoStatus(videoData);

        const isReportComplete = reportData.generationStatus === "completed";
        const isVideoDone =
          videoData.isMerged ||
          videoData.statusOfRecording === "completed" ||
          videoData.statusOfRecording === null;

        if (isReportComplete && isVideoDone && intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch report");
      } finally {
        if (!isPoll) {
          setLoading(false);
        }
      }
    };

    fetchReport(false);
    intervalId = setInterval(() => fetchReport(true), POLL_INTERVAL_MS);

    return () => {
      isCancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [id]);

  useEffect(() => {
    const email = report?.interviewDetails?.user_email;

    if (!email) {
      setProviderProfile(null);
      setProviderProfileUrl(null);
      return;
    }

    let isCancelled = false;

    const fetchProfile = async () => {
      try {
        const response = await fetch(
          `${PROVIDER_ENDPOINT}/api/v1/user/getUserByEmail?email=${encodeURIComponent(email)}`
        );
        if (!response.ok) return;

        const payload = (await response.json()) as {
          success?: boolean;
          data?: ProviderProfile;
        };

        if (!payload?.success || !payload.data || isCancelled) return;

        setProviderProfile(payload.data);
        setProviderProfileUrl(
          payload.data.username
            ? `${PROVIDER_ENDPOINT}/profile/${payload.data.username}`
            : null
        );
      } catch {
        // best-effort; ignore profile fetch errors
      }
    };

    fetchProfile();

    return () => {
      isCancelled = true;
    };
  }, [report?.interviewDetails?.user_email]);

  const formattedReport = useMemo(() => {
    if (!report) return null;

    const interviewDetails = report.interviewDetails;
    const usernameFromData =
      providerProfile?.username ??
      interviewDetails?.user_email;
    const profileUrlFromUsername = usernameFromData
      ? `${PROVIDER_ENDPOINT}/profile/${usernameFromData}`
      : undefined;
    
    const profile = providerProfile
      ? {
          name:
            providerProfile.full_name ??
            providerProfile.username ??
            interviewDetails?.user_name,
          username:
            providerProfile.username ??
            providerProfile.email ??
            interviewDetails?.user_email,
          profile_picture: providerProfile.profile_image_url,
          profile_url: profileUrlFromUsername ?? providerProfileUrl ?? undefined,
        }
      : interviewDetails
        ? {
            name: interviewDetails.user_name,
            username: interviewDetails.user_email,
            profile_url: profileUrlFromUsername,
          }
        : undefined;

    // Transform interview messages for transcript
    const transcriptMessages = Array.isArray(report.interviewMessages)
      ? report.interviewMessages.map((msg) => ({
          _id: msg.id,
          role: msg.role,
          message: msg.message,
          isInstructionMessage: Boolean(msg.isInstruction),
          isToolCall: false,
        }))
      : [];

    return {
      interviewId: report.interviewId,
      generationStatus: report.generationStatus,
      problem: report.problem,
      submission: report.submission,
      evaluation: report.evaluation,
      profile,
      interviewDetails: interviewDetails || undefined,
      messages: transcriptMessages,
      s3_recording_url: videoStatus?.mergedUrl ? [videoStatus.mergedUrl] : [],
      recording: videoStatus
        ? {
            mergedUrl: videoStatus.mergedUrl,
            statusOfRecording: videoStatus.statusOfRecording,
          }
        : undefined,
    };
  }, [report, videoStatus, providerProfile, providerProfileUrl]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f6f8fb]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Fetching the latest report…</p>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f6f8fb]">
        <div className="text-center">
          <p className="text-red-500 mb-4">Error: {error ?? "No report found"}</p>
          <p className="text-gray-500">Please verify the report ID and try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#f6f8fb]">
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10 py-8 md:py-10">
        <ReportComponent report={formattedReport} />
      </div>
    </div>
  );
};

export default Report;
